import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Skeleton,
  Alert,
  AlertTitle,
  Link,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassIcon,
  Pending as PendingIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

/**
 * 섹션 카드 컴포넌트
 * - 제목과 아이콘이 있는 헤더
 * - 내용 영역
 */
export const InfoCard = ({ title, icon, children, action, collapsible = false }) => (
  <Paper 
    elevation={0} 
    sx={{ 
      mb: 2.5, 
      border: '1px solid', 
      borderColor: 'divider',
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    <Box 
      sx={{ 
        px: 2.5, 
        py: 1.5, 
        bgcolor: 'grey.50', 
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>}
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
    <Box sx={{ p: 2.5 }}>
      {children}
    </Box>
  </Paper>
);

/**
 * 라벨-값 쌍 표시 컴포넌트 (세로 배치)
 */
export const InfoItem = ({ label, value, xs = 3, sm, md, highlight = false, tooltip }) => {
  const content = (
    <Grid item xs={xs} sm={sm} md={md}>
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography 
        variant="body1" 
        fontWeight={highlight ? 600 : 400}
        color={highlight ? 'primary.main' : 'text.primary'}
        sx={{ 
          wordBreak: 'break-word',
          whiteSpace: 'pre-line',
        }}
      >
        {value || '-'}
      </Typography>
    </Grid>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

/**
 * 상태 표시 Chip
 */
export const StatusChip = ({ status, size = 'small' }) => {
  const statusConfig = {
    'N': { label: '미결재', color: 'default', icon: <PendingIcon fontSize="small" /> },
    'S': { label: '진행중', color: 'warning', icon: <HourglassIcon fontSize="small" /> },
    'Y': { label: '완료', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    'R': { label: '반려', color: 'error', icon: <CancelIcon fontSize="small" /> },
  };
  
  const config = statusConfig[status] || statusConfig['N'];
  
  return (
    <Chip 
      icon={config.icon}
      label={config.label} 
      color={config.color}
      size={size}
      variant="outlined"
      sx={{ fontWeight: 500 }}
    />
  );
};

/**
 * 재사용 가능한 DatePicker 컴포넌트
 */
export const FormDatePicker = ({ 
  name, 
  label, 
  formik, 
  required = false,
  size = 'small',
  minDate,
  maxDate,
  disabled = false,
}) => {
  const value = formik.values[name];
  const error = formik.touched[name] && Boolean(formik.errors[name]);
  const helperText = formik.touched[name] && formik.errors[name];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
      <DatePicker
        label={label}
        format="YYYY-MM-DD"
        value={value ? dayjs(value) : null}
        onChange={(newValue) => {
          formik.setFieldValue(name, newValue ? dayjs(newValue).format('YYYY-MM-DD') : null);
        }}
        minDate={minDate ? dayjs(minDate) : undefined}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        disabled={disabled}
        slotProps={{
          textField: {
            required,
            fullWidth: true,
            size,
            error,
            helperText,
            id: name,
            name,
          },
        }}
      />
    </LocalizationProvider>
  );
};

/**
 * 날짜 기간 입력 컴포넌트
 */
export const DateRangeInput = ({ 
  startName, 
  endName, 
  startLabel, 
  endLabel, 
  formik,
  required = false,
}) => {
  const startDate = formik.values[startName];
  const endDate = formik.values[endName];
  
  // 기간 계산 (일)
  const duration = startDate && endDate 
    ? dayjs(endDate).diff(dayjs(startDate), 'day') + 1
    : null;

  // 기간 계산 (월)
  const months = startDate && endDate 
    ? dayjs(endDate).diff(dayjs(startDate), 'month')
    : null;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
      <FormDatePicker 
        name={startName} 
        label={startLabel} 
        formik={formik} 
        required={required}
        maxDate={endDate}
      />
      <Box sx={{ 
        display: { xs: 'none', sm: 'flex' }, 
        alignItems: 'center', 
        height: 40,
        color: 'text.secondary' 
      }}>
        ~
      </Box>
      <FormDatePicker 
        name={endName} 
        label={endLabel} 
        formik={formik} 
        required={required}
        minDate={startDate}
      />
      {duration !== null && (
        <Chip 
          label={months > 0 ? `${months}개월 (${duration}일)` : `${duration}일`}
          size="small" 
          color="info"
          variant="outlined"
          sx={{ height: 40, alignSelf: { xs: 'flex-start', sm: 'center' } }}
        />
      )}
    </Stack>
  );
};

/**
 * 금액 표시 (콤마 포맷)
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || num === '') return '-';
  return Number(num).toLocaleString('ko-KR');
};

/**
 * 금액 입력 with 비율 표시
 */
export const AmountWithPercentInput = ({ 
  name, 
  label, 
  formik, 
  totalAmount,
  size = 'small',
  disabled = false,
  InputComponent,
}) => {
  const value = formik.values[name] || 0;
  const percent = totalAmount ? ((Number(value) / totalAmount) * 100).toFixed(1) : 0;
  
  return (
    <TextField
      fullWidth
      size={size}
      label={label}
      id={name}
      name={name}
      value={value}
      onChange={(e) => formik.setFieldValue(name, e.target.value)}
      disabled={disabled}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
      InputProps={{
        inputComponent: InputComponent,
        endAdornment: totalAmount ? (
          <InputAdornment position="end">
            <Chip 
              label={`${percent}%`} 
              size="small" 
              color={Number(percent) >= 15 ? 'success' : Number(percent) >= 5 ? 'warning' : 'error'}
              variant="outlined"
              sx={{ minWidth: 60 }}
            />
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

/**
 * 매출이익 시각화 바
 */
export const ProfitVisualization = ({ totalSales, profit, cost, internalCost }) => {
  if (!totalSales || totalSales === 0) return null;
  
  const finalProfit = Number(profit || 0) - Number(cost || 0) - Number(internalCost || 0);
  const profitPercent = (Number(profit || 0) / totalSales) * 100;
  const costPercent = (Number(cost || 0) / totalSales) * 100;
  const internalPercent = (Number(internalCost || 0) / totalSales) * 100;
  const finalPercent = (finalProfit / totalSales) * 100;

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom fontWeight={600}>
        매출이익 구조
      </Typography>
      
      {/* 바 차트 */}
      <Box sx={{ 
        display: 'flex', 
        height: 24, 
        borderRadius: 1, 
        overflow: 'hidden',
        mb: 1.5,
        bgcolor: 'grey.200',
      }}>
        {profitPercent > 0 && (
          <Tooltip title={`매출이익: ${formatNumber(profit)} (${profitPercent.toFixed(1)}%)`}>
            <Box sx={{ 
              width: `${Math.max(profitPercent, 5)}%`, 
              bgcolor: 'success.main',
              transition: 'width 0.3s ease',
            }} />
          </Tooltip>
        )}
        {costPercent > 0 && (
          <Tooltip title={`영업비용: ${formatNumber(cost)} (${costPercent.toFixed(1)}%)`}>
            <Box sx={{ 
              width: `${Math.max(costPercent, 3)}%`, 
              bgcolor: 'warning.main',
              transition: 'width 0.3s ease',
            }} />
          </Tooltip>
        )}
        {internalPercent > 0 && (
          <Tooltip title={`내부거래: ${formatNumber(internalCost)} (${internalPercent.toFixed(1)}%)`}>
            <Box sx={{ 
              width: `${Math.max(internalPercent, 3)}%`, 
              bgcolor: 'error.light',
              transition: 'width 0.3s ease',
            }} />
          </Tooltip>
        )}
      </Box>

      {/* 범례 */}
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: 'success.main', borderRadius: 0.5 }} />
          <Typography variant="caption">매출이익</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: 0.5 }} />
          <Typography variant="caption">영업비용</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: 'error.light', borderRadius: 0.5 }} />
          <Typography variant="caption">내부거래</Typography>
        </Box>
      </Stack>

      {/* 최종 이익 */}
      <Box sx={{ 
        mt: 2, 
        pt: 1.5, 
        borderTop: '1px dashed',
        borderColor: 'divider',
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography variant="body2" fontWeight={600}>
          최종 매출이익
        </Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" color={finalProfit >= 0 ? 'success.main' : 'error.main'} fontWeight={700}>
            {formatNumber(finalProfit)}원
          </Typography>
          <Typography variant="caption" color="text.secondary">
            이익률 {finalPercent.toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * 폼 에러 요약 컴포넌트
 */
export const FormErrorSummary = ({ errors, touched }) => {
  const fieldLabels = {
    expDay: '계약일',
    siDt: '계약완료일',
    mtnFreeStrtDt: '무상유지보수 시작일',
    mtnFreeEndDt: '무상유지보수 종료일',
    collCondt: '수금조건',
    payCondt: '지급조건',
    roProfitAmt: '매출이익',
    roCostAmt: '영업비용',
  };

  const errorList = Object.entries(errors)
    .filter(([key]) => touched[key])
    .map(([key, msg]) => ({ 
      field: key, 
      label: fieldLabels[key] || key,
      message: typeof msg === 'string' ? msg : '입력값을 확인하세요',
    }));

  if (errorList.length === 0) return null;

  return (
    <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
      <AlertTitle>입력 오류가 있습니다 ({errorList.length}건)</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {errorList.map(({ field, label, message }) => (
          <li key={field}>
            <Link 
              component="button"
              variant="body2"
              onClick={() => {
                const element = document.getElementById(field);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  element.focus();
                }
              }}
              sx={{ cursor: 'pointer' }}
            >
              {label}: {message}
            </Link>
          </li>
        ))}
      </Box>
    </Alert>
  );
};

/**
 * Skeleton 로딩 컴포넌트
 */
export const OrderBookSkeleton = () => (
  <Box sx={{ p: 2 }}>
    {/* 개요 섹션 */}
    <Paper sx={{ mb: 2, p: 2 }}>
      <Skeleton variant="text" width={120} height={28} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Grid item xs={2} key={i}>
            <Skeleton variant="text" width="60%" height={16} />
            <Skeleton variant="text" width="80%" height={24} />
          </Grid>
        ))}
      </Grid>
    </Paper>

    {/* 기본정보 섹션 */}
    <Paper sx={{ mb: 2, p: 2 }}>
      <Skeleton variant="text" width={100} height={28} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
          <Grid item xs={3} key={i}>
            <Skeleton variant="text" width="40%" height={16} />
            <Skeleton variant="text" width="70%" height={24} />
          </Grid>
        ))}
      </Grid>
    </Paper>

    {/* 매출이익 섹션 */}
    <Paper sx={{ mb: 2, p: 2 }}>
      <Skeleton variant="text" width={140} height={28} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
    </Paper>

    {/* 결재 섹션 */}
    <Paper sx={{ p: 2 }}>
      <Skeleton variant="text" width={80} height={28} sx={{ mb: 2 }} />
      <Stack direction="row" spacing={2}>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} variant="circular" width={48} height={48} />
        ))}
      </Stack>
    </Paper>
  </Box>
);

/**
 * 변경사항 표시 인디케이터
 */
export const UnsavedChangesIndicator = ({ hasChanges }) => {
  if (!hasChanges) return null;
  
  return (
    <Chip
      icon={<InfoIcon fontSize="small" />}
      label="저장되지 않은 변경사항"
      color="warning"
      size="small"
      variant="outlined"
    />
  );
};

export default {
  InfoCard,
  InfoItem,
  StatusChip,
  FormDatePicker,
  DateRangeInput,
  formatNumber,
  AmountWithPercentInput,
  ProfitVisualization,
  FormErrorSummary,
  OrderBookSkeleton,
  UnsavedChangesIndicator,
};
